import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet'
import { useNavigate } from 'react-router-dom'
import type { Artist } from '@/types/models'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { getCountryFlagHtml } from '@/lib/countryFlags'
import { useTheme } from '@/contexts/ThemeContext'

interface ArtistCluster {
  id: string
  artists: (Artist & { adjustedLat: number; adjustedLon: number })[]
  centerLat: number
  centerLon: number
  isCluster: boolean
}

// Create cluster icon
const createClusterIcon = (count: number, country?: string) => {
  const countryFlag = getCountryFlagHtml(country)
  const iconHtml = `
    <div class="cluster-marker">
      <div class="marker-content">
        <div class="cluster-avatar">
          <div class="cluster-flag">${countryFlag}</div>
        </div>
        <div class="artist-info">
          <div class="artist-name">${count} Artists</div>
        </div>
      </div>
      <div class="marker-arrow"></div>
    </div>
  `

  return L.divIcon({
    html: iconHtml,
    className: 'cluster-marker-wrapper',
    iconSize: [140, 36],
    iconAnchor: [70, 36],
    popupAnchor: [0, -36]
  })
}

// Create custom icon with artist info
const createCustomIcon = (artist: Artist) => {
  if (!artist) {
    console.error('Artist is undefined in createCustomIcon')
    return createClusterIcon(1) // Fallback to a cluster icon
  }

  const displayName = artist.displayName || 'Unknown'

  const avatarUrl = artist.compressedAvatarUrl || artist.avatarUrl
  
  const iconHtml = `
    <div class="custom-marker">
      <div class="marker-content">
        <div class="avatar-container">
          ${avatarUrl 
            ? `<img src="${avatarUrl}" alt="${displayName}" class="avatar-image" />` 
            : `<div class="avatar-placeholder">${(artist.displayName?.[0] || '?').toUpperCase()}</div>`
          }
        </div>
        <div class="artist-info">
          <div class="artist-name">${displayName}</div>
        </div>
      </div>
      <div class="marker-arrow"></div>
    </div>
  `

  return L.divIcon({
    html: iconHtml,
    className: 'custom-marker-wrapper',
    iconSize: [120, 36],
    iconAnchor: [60, 36],
    popupAnchor: [0, -36]
  })
}

// Function to spread out artists with the same coordinates
function spreadArtistsWithSameLocation(artists: Artist[]): (Artist & { adjustedLat: number; adjustedLon: number })[] {
  const artistsWithAdjustedCoordinates = artists.map(artist => ({
    ...artist,
    adjustedLat: artist.location!.lat!,
    adjustedLon: artist.location!.lon!
  }))

  // Group artists by their exact coordinates
  const coordinateGroups: Record<string, (Artist & { adjustedLat: number; adjustedLon: number })[]> = {}
  
  artistsWithAdjustedCoordinates.forEach(artist => {
    const coordKey = `${artist.location!.lat!},${artist.location!.lon!}`
    if (!coordinateGroups[coordKey]) {
      coordinateGroups[coordKey] = []
    }
    coordinateGroups[coordKey].push(artist)
  })

  // Spread out artists that share the same coordinates
  Object.values(coordinateGroups).forEach(group => {
    if (group.length > 1) {
      const baseRadius = 0.0008 // Approximately 90 meters in degrees
      const angleStep = (2 * Math.PI) / group.length
      
      group.forEach((artist, index) => {
        if (index === 0) return // Keep the first artist at the original position
        
        const angle = angleStep * index
        const offsetLat = baseRadius * Math.cos(angle)
        const offsetLon = baseRadius * Math.sin(angle)
        
        artist.adjustedLat = artist.location!.lat! + offsetLat
        artist.adjustedLon = artist.location!.lon! + offsetLon
      })
    }
  })

  return artistsWithAdjustedCoordinates
}

// Function to cluster artists based on zoom level
function clusterArtists(
  artists: (Artist & { adjustedLat: number; adjustedLon: number })[],
  zoomLevel: number
): ArtistCluster[] {
  // At zoom level 10 and above, show individual artists (no clustering)
  if (zoomLevel >= 10) {
    return artists.map(artist => ({
      id: artist.id,
      artists: [artist],
      centerLat: artist.adjustedLat,
      centerLon: artist.adjustedLon,
      isCluster: false
    }))
  }

  // Calculate clustering distance based on zoom level
  // Lower zoom = larger clustering radius
  // Roughly 50km at zoom 7, scaling down as we zoom in
  const baseRadiusKm = 50
  const radiusKm = baseRadiusKm * Math.pow(0.5, Math.max(0, zoomLevel - 7))
  
  // Convert km to degrees (rough approximation: 1 degree ≈ 111km)
  const radiusDegrees = radiusKm / 111

  const clusters: ArtistCluster[] = []
  const processedArtists = new Set<string>()

  artists.forEach(artist => {
    if (processedArtists.has(artist.id)) return

    const nearbyArtists = artists.filter(otherArtist => {
      if (processedArtists.has(otherArtist.id)) return false
      
      const latDiff = artist.adjustedLat - otherArtist.adjustedLat
      const lonDiff = artist.adjustedLon - otherArtist.adjustedLon
      const distance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff)
      
      return distance <= radiusDegrees
    })

    nearbyArtists.forEach(a => processedArtists.add(a.id))

    if (nearbyArtists.length > 1) {
      // Create cluster
      const avgLat = nearbyArtists.reduce((sum, a) => sum + a.adjustedLat, 0) / nearbyArtists.length
      const avgLon = nearbyArtists.reduce((sum, a) => sum + a.adjustedLon, 0) / nearbyArtists.length
      
      clusters.push({
        id: `cluster-${artist.id}`,
        artists: nearbyArtists,
        centerLat: avgLat,
        centerLon: avgLon,
        isCluster: true
      })
    } else {
      // Individual artist
      clusters.push({
        id: artist.id,
        artists: nearbyArtists,
        centerLat: artist.adjustedLat,
        centerLon: artist.adjustedLon,
        isCluster: false
      })
    }
  })

  return clusters
}

// Component to handle map bounds adjustment (only on initial load)
function MapBoundsHandler({ 
  artists, 
  hasInitialized 
}: { 
  artists: (Artist & { adjustedLat: number; adjustedLon: number })[]
  hasInitialized: boolean
}) {
  const map = useMap()

  useEffect(() => {
    // Only adjust bounds if not yet initialized and we have artists
    if (hasInitialized || artists.length === 0) return

    const validCoordinates = artists.map(artist => [artist.adjustedLat, artist.adjustedLon] as [number, number])

    if (validCoordinates.length === 0) return

    if (validCoordinates.length === 1) {
      // Single artist - center on them with reasonable zoom
      map.setView(validCoordinates[0], 15)
    } else {
      // Multiple artists - shrink bounds to create tighter fit
      const originalBounds = L.latLngBounds(validCoordinates)
      map.fitBounds(originalBounds, { padding: [0, 0] })
    }
  }, [map, artists, hasInitialized])

  return null
}

// Component to track zoom level changes
function ZoomTracker({ 
  onZoomChange 
}: { 
  onZoomChange: (zoom: number) => void 
}) {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom())
    }
  })

  useEffect(() => {
    // Set initial zoom level
    onZoomChange(map.getZoom())
  }, [map, onZoomChange])

  return null
}

// Component to render markers (clusters or individual artists)
function MapMarkersRenderer({ 
  clusters, 
  onMarkerClick 
}: { 
  clusters: ArtistCluster[]
  onMarkerClick: (cluster: ArtistCluster, map: L.Map) => void
}) {
  const map = useMap()

  return (
    <>
      {clusters.map((cluster) => {
        // Safety check: ensure cluster has artists
        if (!cluster.artists || cluster.artists.length === 0) {
          return null
        }

        const firstArtistCountry = cluster.artists[0]?.location?.country || 'Unknown'

        return (
          <Marker 
            key={cluster.id} 
            position={[cluster.centerLat, cluster.centerLon]}
            icon={cluster.isCluster 
              ? createClusterIcon(cluster.artists.length, firstArtistCountry)
              : createCustomIcon(cluster.artists[0])
            }
            eventHandlers={{
              click: () => onMarkerClick(cluster, map)
            }}
          />
        )
      })}
    </>
  )
}

interface ArtistsMapProps {
  artists: Artist[]
  isLoading?: boolean
}

// Default coordinates for cities
const CITY_COORDINATES: Record<string, [number, number]> = {
  'Helsinki': [60.1699, 24.9384],
  'Tampere': [61.4991, 23.7871],
  'Tallinn': [59.4370, 24.7536],
  'Amsterdam': [52.3676, 4.9041],
}

export function ArtistsMap({ artists: inputArtists, isLoading = false }: ArtistsMapProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentThemeMode } = useTheme()
  const [artists, setArtists] = useState<(Artist & { adjustedLat: number; adjustedLon: number })[]>([])
  const [zoomLevel, setZoomLevel] = useState(7)
  const [clusters, setClusters] = useState<ArtistCluster[]>([])
  const [hasInitialized, setHasInitialized] = useState(false)

  useEffect(() => {
    const artistsWithAdjustedCoordinates = spreadArtistsWithSameLocation(inputArtists)
    setArtists(artistsWithAdjustedCoordinates)
  }, [inputArtists])

  useEffect(() => {
    // Update clusters when artists or zoom level changes
    const newClusters = clusterArtists(artists, zoomLevel)
    // Filter out any invalid clusters (safety check)
    const validClusters = newClusters.filter(cluster => 
      cluster.artists && cluster.artists.length > 0
    )
    setClusters(validClusters)
    
    // Mark as initialized after first clustering
    if (!hasInitialized && artists.length > 0) {
      setHasInitialized(true)
    }
  }, [artists, zoomLevel, hasInitialized])

  const handleZoomChange = (zoom: number) => {
    setZoomLevel(zoom)
  }

  const handleMarkerClick = (cluster: ArtistCluster, map: L.Map) => {
    if (cluster.isCluster && cluster.artists.length > 1) {
      // Zoom in on cluster
      const bounds = L.latLngBounds(
        cluster.artists.map(a => [a.adjustedLat, a.adjustedLon] as [number, number])
      )
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
    } else {
      // Navigate to individual artist
      navigate(`/artist/${cluster.artists[0].id}`)
    }
  }

  const centerCoordinates = CITY_COORDINATES['Helsinki']

  const artistsWithValidCoordinates = artists

  return (
    <>
      {/* Custom CSS for markers */}
      <style>{`
        /* Cluster styles */
        .cluster-marker-wrapper {
          background: none !important;
          border: none !important;
        }
        
        .cluster-marker {
          position: relative;
          cursor: pointer;
        }
        
        .cluster-avatar {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${currentThemeMode === 'dark' ? '#2a2a2a' : 'white'};
          border-radius: 8px 0 0 8px;
          overflow: hidden;
        }
        
        .cluster-flag {
          font-size: 16px;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .cluster-count-circle {
          font-size: 18px;
          font-weight: 700;
          color: ${currentThemeMode === 'dark' ? '#ffffff' : 'white'};
        }
        
        /* Artist marker styles */
        .custom-marker-wrapper {
          background: none !important;
          border: none !important;
        }
        
        .custom-marker {
          position: relative;
          cursor: pointer;
        }
        
        .marker-content {
          background: ${currentThemeMode === 'dark' ? '#2a2a2a' : 'white'};
          border-radius: 8px;
          box-shadow: 0 2px 8px ${currentThemeMode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'};
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          width: auto;
        }
        
        .custom-marker:hover .marker-content {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px ${currentThemeMode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.2)'};
          border-color: #3b82f6;
        }
        
        .avatar-container {
          width: 36px;
          height: 36px;
          overflow: hidden;
          flex-shrink: 0;
        }
        
        .avatar-image {
          width: 100% !important;
          height: 100%;
          object-fit: cover;
          border-radius: 8px 0 0 8px;
        }
        
        .avatar-placeholder {
          width: 100%;
          height: 100%;
          background: ${currentThemeMode === 'dark' ? '#333333' : '#f3f4f6'};
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          color: ${currentThemeMode === 'dark' ? '#a0a0a0' : '#6b7280'};
        }
        
        .artist-info {
          flex: 1;
          min-width: 0;
        }
        
        .artist-name {
          font-weight: 600;
          font-size: 12px;
          color: ${currentThemeMode === 'dark' ? '#ffffff' : '#111827'};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .marker-arrow {
          position: absolute;
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid ${currentThemeMode === 'dark' ? '#1a1a1a' : '#e5e7eb'};
        }
        
        .marker-arrow::after {
          content: '';
          position: absolute;
          bottom: 1px;
          left: -5px;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid ${currentThemeMode === 'dark' ? '#2a2a2a' : 'white'};
        }
        
        /* Apply grayscale only to map tiles, not markers */
        .leaflet-tile {
          filter: ${currentThemeMode === 'dark' ? 'grayscale(100%) contrast(1.1) brightness(0.65)' : 'grayscale(100%) contrast(1.1)'};
        }
        
        /* Make text labels light in dark mode */
        ${currentThemeMode === 'dark' ? `
          .leaflet-control-attribution,
          .leaflet-tooltip,
          .leaflet-popup-content {
            background-color: rgba(42, 42, 42, 0.95) !important;
            color: #ffffff !important;
          }
          
          .leaflet-popup-content-wrapper {
            background-color: rgba(42, 42, 42, 0.95) !important;
            box-shadow: 0 3px 14px rgba(0, 0, 0, 0.5) !important;
          }
          
          .leaflet-popup-tip {
            background-color: rgba(42, 42, 42, 0.95) !important;
          }
        ` : ''}
      `}</style>
      
      <div
        className="relative w-full min-h-0 h-full max-h-artists-map-mobile rounded-[1.75rem] overflow-hidden border border-border flex flex-col"
      >
        <MapContainer
          center={centerCoordinates}
          style={{ height: '100%', width: '100%', flex: 1, minHeight: 0, borderRadius: 'inherit' }}
          className="z-10 rounded-[1.75rem]"
          zoomControl={window.innerWidth >= 768} // Hide zoom controls on mobile (< 768px)
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          
          {/* Track zoom level changes */}
          <ZoomTracker onZoomChange={handleZoomChange} />
          
          {/* Auto-adjust bounds based on artists (only on initial load) */}
          <MapBoundsHandler artists={artistsWithValidCoordinates} hasInitialized={hasInitialized} />
          
          {/* Render clusters or individual artists */}
          <MapMarkersRenderer 
            clusters={clusters} 
            onMarkerClick={handleMarkerClick}
          />
        </MapContainer>
        
        {artists.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
            <div className="text-center">
              {isLoading ? (
                <>
                  <div className="mb-4 flex justify-center">
                    <div className="w-8 h-8 border-3 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
                  </div>
                  <p className="text-muted-foreground">
                    Loading artists...
                  </p>
                </>
              ) : (
                <p className="text-base font-medium text-muted-foreground leading-snug px-4">
                  {t('artists.emptyList')}
                </p>
              )}
            </div>
          </div>
        )}
        
        {inputArtists.length > 0 && artistsWithValidCoordinates.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
            <div className="text-center">
              <p className="text-muted-foreground mb-2">
                Found {inputArtists.length} artists
              </p>
              <p className="text-sm text-muted-foreground">
                But coordinates are not available yet
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
